import { Nullable } from 'src/types/nullable.type';
import { apiGetJourneyFonts } from 'src/utils/journeyApi';
import { create } from 'zustand';

export interface DynamicFontNode {
  fontFamily: string;
  variants: {
    bold: string;
    light: string;
    regular: string;
  };
}

export interface DynamicJourneyFonts {
  headings: DynamicFontNode;
  paragraphs: DynamicFontNode;
}

export type DynamicFontVariants = 'bold' | 'light' | 'regular';

interface CurrentJourneyFontBase {
  fontFamily: string;
  variant: DynamicFontVariants;
}

export interface CurrentJourneyFont extends CurrentJourneyFontBase {
  url: string;
}

export type CurrentDynamicJourneyFont = {
  headings: CurrentJourneyFont;
  paragraphs: CurrentJourneyFont;
};

export type DynamicFontChangeHandler = (type: keyof DynamicJourneyFonts, font: CurrentJourneyFont) => void;

interface State {
  current: CurrentDynamicJourneyFont;
  setCurrent: DynamicFontChangeHandler;
  resetCurrent: () => void;
  state: 'LOADING' | 'READY' | 'ERROR';
  isReady: boolean;
  isCustomFontStrictlyUsed: boolean;
  options: {
    headings: Array<DynamicFontNode>;
    paragraphs: Array<DynamicFontNode>;
  };
  setOptions: (options: { headings: Array<DynamicFontNode>; paragraphs: Array<DynamicFontNode> }) => void;
  init: (
    dynamicFonts: Nullable<{
      headings: { font_family: string; font_variant: string };
      paragraphs: { font_family: string; font_variant: string };
    }>
  ) => Promise<void>;
  setIsCustomFontStrictlyUsed: (isStrict: boolean) => void;
}

const CUSTOM_FONT_USED_ORGS = ['jny', 'journey', 'urbanuncut'];
export const isCustomFontStrictlyUsedForOrg = (orgSlug: string) => {
  return CUSTOM_FONT_USED_ORGS.includes(orgSlug);
};

export const INITIAL_DEFAULT_DYNAMIC_FONTS: CurrentDynamicJourneyFont = {
  headings: {
    fontFamily: 'CircularXX',
    variant: 'regular',
    url: '',
  },
  paragraphs: {
    fontFamily: 'CircularXX',
    variant: 'regular',
    url: '',
  },
};

export const useJourneyDynamicFontStore = create<State>((set, get) => ({
  current: INITIAL_DEFAULT_DYNAMIC_FONTS,
  isCustomFontStrictlyUsed: false,
  init: async (curr) => {
    try {
      const fontsResponse = await apiGetJourneyFonts();
      if (fontsResponse.error) {
        set({ state: 'ERROR' });
        return;
      }

      set({
        isReady: true,
        state: 'READY',
        options: {
          headings: fontsResponse.fonts,
          paragraphs: fontsResponse.fonts,
        },
      });

      if (curr) {
        const { headings, paragraphs } = curr;
        const headingFontUrl =
          fontsResponse.fonts.find((font: any) => font.fontFamily === headings.font_family)?.variants[
            headings.font_variant.toLowerCase()
          ] || '';
        const paragraphFontUrl =
          fontsResponse.fonts.find((font: any) => font.fontFamily === paragraphs.font_family)?.variants[
            paragraphs.font_variant.toLowerCase()
          ] || '';
        set({
          current: {
            headings: {
              fontFamily: headings.font_family,
              variant: headings.font_variant as DynamicFontVariants,
              url: headingFontUrl,
            },
            paragraphs: {
              fontFamily: paragraphs.font_family,
              variant: paragraphs.font_variant as DynamicFontVariants,
              url: paragraphFontUrl,
            },
          },
        });
      }
    } catch (err) {
      set({ state: 'ERROR' });
      console.info(err);
    }
  },
  setIsCustomFontStrictlyUsed: (isStrict: boolean) => {
    set({ isCustomFontStrictlyUsed: isStrict });
  },
  isReady: false,
  setCurrent: (type, font) => {
    set({
      current: {
        ...get().current,
        [type]: font,
      },
    });
  },
  resetCurrent: () => {
    const existingHeadingStyles = Array.from(document.querySelectorAll(`style[id^="dynamicFont-headings"]`));
    const existingParagraphStyles = Array.from(document.querySelectorAll(`style[id^="dynamicFont-paragraphs"]`));
    [...existingHeadingStyles, ...existingParagraphStyles].forEach((style) => style.remove());
    set({ current: INITIAL_DEFAULT_DYNAMIC_FONTS });
  },
  state: 'LOADING',
  options: {
    headings: [],
    paragraphs: [],
  },
  setOptions: (options) => set({ options, state: 'READY' }),
}));
